import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clapperboard, FileText, ImageIcon, PlayCircle } from 'lucide-react';
import {
  getMeaningMemberById,
  type MeaningPhotoMoment,
  type MeaningTextMoment,
  type MeaningVideoMoment,
} from '../config/meaningData';
import { useTranslation } from '../i18n/hooks';
import MeaningVideoPlayer from '../components/meaning/MeaningVideoPlayer';
import SmartImage from '../components/SmartImage';
import FilmStripGallery from '../components/extra/FilmStripGallery';

const MeaningMemberDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { monthId, memberId } = useParams<{ monthId: string; memberId: string }>();
  const { language, t } = useTranslation();
  const isZh = language === 'zh-CN';

  const member = monthId && memberId ? getMeaningMemberById(monthId, memberId) : undefined;

  if (!member || !monthId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 py-14">
          <button
            type="button"
            onClick={() => navigate('/meaning')}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/60 border border-slate-200"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('meaning.member.back') as string}
          </button>
          <div className="mt-6 rounded-3xl border border-white/80 bg-white/65 p-8">
            <h1 className="text-xl font-bold">{t('meaning.member.notFound') as string}</h1>
          </div>
        </div>
      </div>
    );
  }

  const photoItems = member.submissions.filter((item): item is MeaningPhotoMoment => item.type === 'photo');
  const videoItems = member.submissions.filter((item): item is MeaningVideoMoment => item.type === 'video');
  const textItems = member.submissions.filter((item): item is MeaningTextMoment => item.type === 'text');
  const messagePhotos = photoItems.filter((item) => item.tags.includes('留言') || item.tags.includes('文字'));
  const rollPhotos = photoItems.filter((item) => !messagePhotos.includes(item));
  const heroPhoto = rollPhotos[0]?.photo || messagePhotos[0]?.photo;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-indigo-50 text-slate-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          type="button"
          onClick={() => navigate(`/meaning/${monthId}`)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/60 border border-slate-200 hover:bg-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('meaning.member.backMonth') as string}
        </button>

        <div className="mt-6 overflow-hidden rounded-[2rem] border border-white/80 bg-white/65 backdrop-blur shadow-[0_25px_70px_rgba(2,132,199,0.10)]">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.15fr)]">
            <div className="relative min-h-[320px] overflow-hidden bg-slate-950">
              {heroPhoto ? (
                <SmartImage
                  asset={heroPhoto}
                  variant="preview"
                  alt={member.name}
                  className="absolute inset-0 h-full w-full object-cover opacity-85"
                />
              ) : null}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
              <div className="absolute inset-x-0 top-0 h-2 bg-[repeating-linear-gradient(90deg,rgba(255,255,255,0.9)_0_16px,rgba(16,185,129,0.45)_16px_22px,rgba(255,255,255,0.9)_22px_38px)] opacity-80" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/35 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.2em] backdrop-blur">
                  <Clapperboard className="h-3.5 w-3.5" />
                  {isZh ? '四月胶片档案' : 'April Film Archive'}
                </p>
                <h1 className="mt-3 text-3xl font-black tracking-tight">{member.name}</h1>
                <p className="mt-2 text-sm text-white/78 leading-relaxed">{member.tagline[isZh ? 'zh' : 'en']}</p>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-emerald-700/70 font-semibold">
                    {member.city}
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">{member.keyword[isZh ? 'zh' : 'en']}</h2>
                </div>
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-emerald-100 bg-emerald-50 text-lg font-black text-emerald-800">
                  {member.name.slice(0, 1)}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                  <ImageIcon className="h-4 w-4 text-emerald-600" />
                  <p className="mt-2 text-2xl font-black">{rollPhotos.length}</p>
                  <p className="text-xs text-slate-500">{isZh ? '照片' : 'Photos'}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                  <PlayCircle className="h-4 w-4 text-sky-600" />
                  <p className="mt-2 text-2xl font-black">{videoItems.length}</p>
                  <p className="text-xs text-slate-500">{isZh ? '短片' : 'Clips'}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                  <FileText className="h-4 w-4 text-rose-600" />
                  <p className="mt-2 text-2xl font-black">{messagePhotos.length + textItems.length}</p>
                  <p className="text-xs text-slate-500">{isZh ? '文字' : 'Notes'}</p>
                </div>
              </div>

              <p className="mt-6 border-l-2 border-emerald-200 pl-4 text-sm leading-relaxed text-slate-600">
                {isZh
                  ? '这一页按胶卷阅读，不再把素材简单摊平。先从主片进入，再用下方胶片舞台和缩略图慢慢切换。'
                  : 'This page reads like a roll instead of a flat asset list: enter through the lead frame, then move through the stage and thumbnails below.'}
              </p>
            </div>
          </div>

          <div className="p-6 md:p-8">
            {member.submissions.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200/90 bg-gradient-to-br from-white/70 to-slate-50/50 px-6 py-10 text-center">
                <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
                  {t('meaning.member.emptySubmissions') as string}
                </p>
              </div>
            ) : (
              <div className="space-y-10">
                {rollPhotos.length > 0 ? (
                  <section>
                    <div className="mb-4 flex items-end justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-black">{isZh ? '胶片舞台' : 'Film Stage'}</h2>
                        <p className="mt-1 text-sm text-slate-600">
                          {isZh ? '中间使用预览图，两侧与下方索引用缩略图，加载会轻很多。' : 'The center uses previews while side frames and the index use thumbnails for lighter loading.'}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-slate-400">{rollPhotos.length} frames</span>
                    </div>
                    <FilmStripGallery
                      photos={rollPhotos.map((item) => item.photo)}
                      layout="stage"
                      showHint
                    />
                  </section>
                ) : null}

                {rollPhotos.length > 0 ? (
                  <section>
                    <h2 className="text-lg font-black">{isZh ? '全部画格' : 'All Frames'}</h2>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                      {rollPhotos.map((item, index) => (
                        <div key={item.id} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white/75 shadow-sm">
                          <div className="aspect-[3/4] overflow-hidden bg-slate-100">
                            <SmartImage
                              asset={item.photo}
                              variant="thumbnail"
                              alt={item.zhCaption || item.enCaption || `frame ${index + 1}`}
                              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                            />
                          </div>
                          <div className="flex items-center justify-between px-3 py-2 text-xs text-slate-500">
                            <span>{String(index + 1).padStart(2, '0')}</span>
                            <span>{isZh ? item.zhCaption : item.enCaption}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null}

                {videoItems.length > 0 ? (
                  <section>
                    <h2 className="text-lg font-black">{isZh ? '短片记录' : 'Video Notes'}</h2>
                    <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                      {videoItems.map((item) => {
                        const first = item.videoSources[0];
                        return (
                          <div key={item.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white/75">
                            <div className="aspect-video bg-slate-100">
                              {first ? <MeaningVideoPlayer source={first} variant="card" className="h-full w-full object-cover" /> : null}
                            </div>
                            <div className="p-4 text-xs text-slate-500">{item.tags.join(' · ')}</div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ) : null}

                {(messagePhotos.length > 0 || textItems.length > 0) ? (
                  <section>
                    <h2 className="text-lg font-black">{isZh ? '文字与留言' : 'Notes'}</h2>
                    <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                      {messagePhotos.map((item) => (
                        <div key={item.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white/75">
                          <SmartImage
                            asset={item.photo}
                            variant="preview"
                            alt={item.zhCaption || item.enCaption || 'note'}
                            className="h-auto w-full object-contain"
                          />
                          <div className="p-4 text-sm text-slate-600">{isZh ? item.zhCaption : item.enCaption}</div>
                        </div>
                      ))}
                      {textItems.map((item) => (
                        <div key={item.id} className="rounded-3xl border border-slate-200 bg-white/75 p-5 text-sm leading-relaxed text-slate-700">
                          {isZh ? item.zhText : item.enText}
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeaningMemberDetailPage;

